/**
 * LeadsDashboard page - marketing leads overview and campaign creation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LeadsTable } from '../../components/marketing/LeadsTable';
import { LeadsFilter } from '../../components/marketing/LeadsFilter';
import { CampaignList } from '../../components/marketing/CampaignList';
import { CampaignComposer } from '../../components/marketing/CampaignComposer';
import {
  getLeads,
  getLeadsSummary,
  getCampaigns,
  createCampaign,
  sendCampaign,
  getDailyLimit,
  type MarketingLead,
  type LeadsSummary,
  type Campaign,
  type DailyLimitStatus,
} from '../../services/marketingApi';
import { useToast } from '../../contexts/ToastContext';
import { Mail, Users, TrendingUp, CheckCircle2 } from 'lucide-react';
import { pageStyles, statStyles } from '../../lib/styles';

export function LeadsDashboard() {
  const navigate = useNavigate();
  const toast = useToast();

  // Filter state
  const [serviceTypes, setServiceTypes] = useState<LeadsSummary[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<string>('');
  const [daysSinceService, setDaysSinceService] = useState(180);

  // Data state
  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [dailyLimit, setDailyLimit] = useState<DailyLimitStatus | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());

  // Loading state
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState('leads');

  // Load service types summary
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const summary = await getLeadsSummary();
        setServiceTypes(summary);
        if (summary.length > 0 && !selectedServiceType) {
          setSelectedServiceType(summary[0].service_type.id);
        }
      } catch {
        toast.error('Error al cargar resumen de leads');
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadSummary();
  }, [toast, selectedServiceType]);

  // Load leads when filter changes
  const loadLeads = useCallback(async () => {
    if (!selectedServiceType) return;

    setIsLoadingLeads(true);
    try {
      const result = await getLeads({
        serviceTypeId: selectedServiceType,
        daysSinceService,
      });
      setLeads(result.leads);
      setSelectedLeads(new Set());
    } catch {
      toast.error('Error al cargar leads');
    } finally {
      setIsLoadingLeads(false);
    }
  }, [selectedServiceType, daysSinceService, toast]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Load campaigns
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const result = await getCampaigns();
        setCampaigns(result.campaigns);
      } catch {
        toast.error('Error al cargar campañas');
      } finally {
        setIsLoadingCampaigns(false);
      }
    };
    loadCampaigns();
  }, [toast]);

  // Load daily limit
  useEffect(() => {
    const loadDailyLimit = async () => {
      try {
        const limit = await getDailyLimit();
        setDailyLimit(limit);
      } catch {
        // Silently fail - not critical
      }
    };
    loadDailyLimit();
  }, []);

  // Get selected service type name
  const selectedServiceTypeName = serviceTypes.find(
    (st) => st.service_type.id === selectedServiceType
  )?.service_type.name;

  // Get recipients from selected leads
  const getRecipientsFromSelection = () => {
    return Array.from(selectedLeads).map((key) => {
      const [customerId, vehicleId] = key.split(':');
      return { customerId, vehicleId };
    });
  };

  // Handle save campaign
  const handleSaveCampaign = async (data: {
    name: string;
    subject: string;
    templateContent: string;
  }) => {
    setIsSaving(true);
    try {
      const campaign = await createCampaign({
        name: data.name,
        serviceTypeId: selectedServiceType,
        subject: data.subject,
        templateContent: data.templateContent,
        recipientIds: getRecipientsFromSelection(),
      });

      toast.success('Campaña guardada como borrador');

      navigate(`/marketing/campaigns/${campaign.id}`);
    } catch {
      toast.error('Error al guardar campaña');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle send campaign from list
  const handleSendCampaign = async (campaignId: string) => {
    try {
      await sendCampaign(campaignId);
      toast.success('Campaña enviada - los emails se están enviando');
      // Refresh campaigns
      const result = await getCampaigns();
      setCampaigns(result.campaigns);
    } catch {
      toast.error('Error al enviar campaña');
    }
  };

  // Stats for header
  const totalLeads = serviceTypes.reduce((sum, st) => sum + st.leads_count, 0);
  const sentCampaigns = campaigns.filter((c) => c.status === 'sent').length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Marketing</h1>
          <p className={pageStyles.subtitle}>
            Campañas de email para recordar mantenimiento
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={statStyles.grid}>
        <div className={statStyles.card}>
          <div className={statStyles.header}>
            <span className={statStyles.label}>Leads</span>
            <Users className={statStyles.icon} />
          </div>
          <div className={statStyles.value}>{totalLeads}</div>
          <p className={statStyles.description}>Clientes disponibles</p>
        </div>

        <div className={statStyles.card}>
          <div className={statStyles.header}>
            <span className={statStyles.label}>Campañas</span>
            <TrendingUp className={statStyles.icon} />
          </div>
          <div className={statStyles.value}>{sentCampaigns}</div>
          <p className={statStyles.description}>Enviadas</p>
        </div>

        <div className={statStyles.card}>
          <div className={statStyles.header}>
            <span className={statStyles.label}>Emails hoy</span>
            <Mail className={statStyles.icon} />
          </div>
          <div className={statStyles.value}>
            {dailyLimit ? `${dailyLimit.sent_today}/${dailyLimit.limit}` : '—'}
          </div>
          <p className={statStyles.description}>
            {dailyLimit ? `${dailyLimit.remaining} restantes` : ''}
          </p>
        </div>

        <div className={statStyles.card}>
          <div className={statStyles.header}>
            <span className={statStyles.label}>Seleccionados</span>
            <CheckCircle2 className={statStyles.icon} />
          </div>
          <div className={statStyles.value}>{selectedLeads.size}</div>
          <p className={statStyles.description}>Para campaña</p>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-8">
          <TabsTrigger value="leads" className="text-xs px-3 py-1">Leads</TabsTrigger>
          <TabsTrigger value="compose" className="text-xs px-3 py-1" disabled={selectedLeads.size === 0}>
            Crear ({selectedLeads.size})
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="text-xs px-3 py-1">Campañas</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="space-y-3 mt-3">
          <LeadsFilter
            serviceTypes={serviceTypes}
            selectedServiceType={selectedServiceType}
            daysSinceService={daysSinceService}
            onServiceTypeChange={setSelectedServiceType}
            onDaysSinceChange={setDaysSinceService}
            isLoading={isLoadingSummary}
          />

          <LeadsTable
            leads={leads}
            selectedLeads={selectedLeads}
            onSelectionChange={setSelectedLeads}
            isLoading={isLoadingLeads}
          />

          {selectedLeads.size > 0 && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setActiveTab('compose')}>
                Crear campaña ({selectedLeads.size})
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="compose" className="mt-3">
          <CampaignComposer
            defaultServiceTypeName={selectedServiceTypeName}
            selectedLeadsCount={selectedLeads.size}
            onSave={handleSaveCampaign}
            isLoading={isSaving}
          />
        </TabsContent>

        <TabsContent value="campaigns" className="mt-3">
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-gray-700">Historial</h2>
            <CampaignList
              campaigns={campaigns}
              isLoading={isLoadingCampaigns}
              onSend={handleSendCampaign}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
